import {
	ActionListCheckbox,
	ActionListDivider,
	ActionListItem,
	DropOverActionList,
} from "client/Components";
import {
	IInputAttachable,
	IObjectInputAttachable,
	IValueInputAttachable
} from "client/Forms/attachable";
import {
	ReactElement,
	useCallback,
	useMemo,
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AvailableMailboxSaveModel, MailboxType } from "client/Api/Email/Mailboxes/mailboxes";
import { OpenItemMenuState, useItemDropOverMenuProps } from "client/Hooks";

type MailboxesManagementActionMenuProps = {
	target: HTMLElement;
	inputAttachable: IObjectInputAttachable<AvailableMailboxSaveModel>;
	inputIndex?: number;
	mailboxesHandler: IValueInputAttachable<readonly AvailableMailboxSaveModel[]>;
	name: string;
	onClose: () => void;
	menuState: OpenItemMenuState;
};

export const MailboxesManagementActionMenu = (
	props: MailboxesManagementActionMenuProps
): ReactElement => {
	const {
		target,
		inputAttachable,
		inputIndex,
		mailboxesHandler,
		name,
		onClose,
		menuState
	} = props;

	const mailbox: AvailableMailboxSaveModel = useMemo(
		(): AvailableMailboxSaveModel => inputAttachable.value,
		[inputAttachable.value]
	);

	const canRemoveItem: boolean = useMemo(
		(): boolean=> mailbox.type === MailboxType.Manual,
		[mailbox.type]
	);

	const canChangeDefaultSendAsValue: boolean = useMemo(
		(): boolean => {
			if (!mailbox.isEnabledAsSendAs) {
				return false;
			}
			if (!mailbox.isEnabledAsDefaultSendAddress) {
				return true;
			}
			if (mailbox.type !== MailboxType.Manual) {
				return false;
			}
			return true;
		},
		[mailbox]
	);

	const canChangeSendAsValue: boolean = useMemo(
		(): boolean => {
			if (!mailbox.isEnabledAsSendAs) {
				return true;
			}
			return !mailbox.isEnabledAsDefaultSendAddress
				|| canChangeDefaultSendAsValue;
		},
		[canChangeDefaultSendAsValue, mailbox]
	);

	const sendAsChangeHandler: (value: boolean) => void = useCallback(
		(value: boolean): void => {
			if (!value) {
				inputAttachable.for("isEnabledAsDefaultSendAddress")
					.reducer.setValue(false);
			}
		},
		[inputAttachable]
	);

	const defaultSendAddressChangeHandler: (value: boolean) => void = useCallback(
		(value: boolean): void => {
			mailboxesHandler.forEach((input) => {
				const isEnabledAsDefaultSendAddressAttachable: IInputAttachable<boolean> = input.for("isEnabledAsDefaultSendAddress");
				if (
					value
					&& isEnabledAsDefaultSendAddressAttachable.value
				) {
					isEnabledAsDefaultSendAddressAttachable.reducer
						.setValue(false);
				} else if (input.value.type !== MailboxType.Manual) {
					isEnabledAsDefaultSendAddressAttachable.reducer
						.setValue(true);

					input.for("isEnabledAsSendAs")
						.reducer
						.setValue(true);
				}
			});
		},
		[mailboxesHandler]
	);

	const removeItemHandler: () => void = useCallback(
		(): void => {
			if(inputIndex){
				mailboxesHandler.reducer
				 .removeAt(inputIndex);
				if(
					inputAttachable.for("isEnabledAsDefaultSendAddress")
						.value
				){
				 	// if the removed item has "isEnabledAsDefaultSendAddress" as true
					// then we set the property to true on the main mailbox
					defaultSendAddressChangeHandler(false);
				}
			}
		},
		[
			defaultSendAddressChangeHandler,
			inputAttachable,
			inputIndex,
			mailboxesHandler.reducer
		]
	);

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(menuState);

	return (
		<DropOverActionList
			trackingId="mailboxes-action-menu"
			target={target}
			onClose={onClose}
			menuTitle={name}
			contentAlignment={contentAlignment}
			targetAlignment={targetAlignment}
			closeOnMouseDown={closeOnMouseDown}
		>
			{canRemoveItem && (
				<>
					<ActionListItem
						onClick={removeItemHandler}
						text={localize("_RemoveSharedMailbox")}
						trackingId="delete"
						icon="trash-can"
					/>
					<ActionListDivider />
				</>
			)}

			<ActionListCheckbox
				text={localize("_ActiveSendAs")}
				{...inputAttachable
					.for("isEnabledAsSendAs")
					.attachToCheckbox()
					.withOnChange(sendAsChangeHandler)
				}
				isDisabled={!canChangeSendAsValue}
				shouldNotAutoCloseContainingDropOver
			/>
			<ActionListCheckbox
				text={localize("_UseAsDefaultSendAddress")}
				{...inputAttachable
					.for("isEnabledAsDefaultSendAddress")
					.attachToCheckbox()
					.withOnChange(defaultSendAddressChangeHandler)
				}
				isDisabled={!canChangeDefaultSendAsValue}
				shouldNotAutoCloseContainingDropOver
			/>
			<ActionListDivider />
			<ActionListCheckbox
				text={localize("_AutomaticallyMarkEmailsAsRead")}
				{...inputAttachable
					.for("isAutoMarkMessageAsReadOnOpen")
					.attachToCheckbox()}
				shouldNotAutoCloseContainingDropOver
			/>
		</DropOverActionList>
	);
};

MailboxesManagementActionMenu.displayName = "MailboxesManagementActionMenu";
